/**
 * React Query hooks for deals data fetching
 */

import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { api } from '../../../../services/api';
import type { DealCategory, SortOption, DealProduct, DealsSummary } from '../types';
import { CACHE_TIMES } from '../utils/constants';

/**
 * Query keys for deals data
 */
export const dealsKeys = {
  all: ['deals'] as const,
  countries: () => [...dealsKeys.all, 'countries'] as const,
  detectCountry: () => [...dealsKeys.all, 'detect-country'] as const,
  summary: (country: string, storeId?: string) =>
    [...dealsKeys.all, 'summary', { country, storeId }] as const,
  products: (filters: DealsQueryFilters) =>
    [...dealsKeys.all, 'products', filters] as const,
  stores: () => [...dealsKeys.all, 'stores'] as const,
};

export interface DealsQueryFilters {
  category: DealCategory;
  country: string;
  storeId?: string;
  search?: string;
  sort: SortOption;
}

/**
 * Fetch available countries for deals
 */
export function useDealsCountries() {
  return useQuery({
    queryKey: dealsKeys.countries(),
    queryFn: () => api.getDealsCountries(),
    staleTime: CACHE_TIMES.COUNTRIES,
    gcTime: CACHE_TIMES.COUNTRIES * 2,
  });
}

/**
 * Detect user's country from Cloudflare
 */
export function useDetectCountry() {
  return useQuery({
    queryKey: dealsKeys.detectCountry(),
    queryFn: () => api.detectCountry(),
    staleTime: CACHE_TIMES.COUNTRIES,
    gcTime: CACHE_TIMES.COUNTRIES * 2,
    retry: 1,
  });
}

/**
 * Fetch deals summary for a country/store
 */
export function useDealsSummary(country: string, storeId?: string) {
  return useQuery({
    queryKey: dealsKeys.summary(country, storeId),
    queryFn: () => api.getDealsSummary(country, storeId),
    enabled: Boolean(country),
    staleTime: CACHE_TIMES.SUMMARY,
    gcTime: CACHE_TIMES.SUMMARY * 2,
    select: (data): DealsSummary | undefined => {
      if (!data) return undefined;
      return {
        last_chance: data.last_chance,
        family_offers: data.family_offers,
        limited_time: data.limited_time,
        as_is: data.as_is,
      };
    },
  });
}

/**
 * Fetch deal products with infinite scroll pagination
 */
export function useDealsProducts(filters: DealsQueryFilters, enabled = true) {
  return useInfiniteQuery({
    queryKey: dealsKeys.products(filters),
    queryFn: async ({ pageParam }) => {
      const response = await api.getDealsProducts({
        category: filters.category === 'all' ? undefined : filters.category,
        country: filters.country,
        store_id: filters.storeId,
        search: filters.search || undefined,
        sort: filters.sort,
        cursor: pageParam || undefined,
        limit: 24,
      });

      return response;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.next_cursor : undefined,
    enabled: enabled && Boolean(filters.country),
    staleTime: CACHE_TIMES.PRODUCTS,
    gcTime: CACHE_TIMES.PRODUCTS * 2,
  });
}

/**
 * Get all products from infinite query pages
 */
export function getAllProducts(
  data: ReturnType<typeof useDealsProducts>['data']
): DealProduct[] {
  if (!data?.pages) return [];
  return data.pages.flatMap((page) => page.products as DealProduct[]);
}

/**
 * Fetch all stores
 */
export function useStores() {
  return useQuery({
    queryKey: dealsKeys.stores(),
    queryFn: () => api.getStores(),
    staleTime: CACHE_TIMES.STORES,
    gcTime: CACHE_TIMES.STORES * 2,
    select: (data) => data?.data || [],
  });
}

/**
 * Get stores filtered by country
 */
export function useStoresByCountry(country: string) {
  const { data: allStores, ...rest } = useStores();

  const filteredStores =
    allStores?.filter(
      (store) => store.country.toUpperCase() === country.toUpperCase()
    ) || [];

  return {
    data: filteredStores,
    ...rest,
  };
}
